import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import Prompts from "../../API/Prompts";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../Router";
import { generateRowId } from "../../Utility";
import GenerationSettingsAI from "../Settings/Components/Generation/GenerationSettingsAI";
import AgentBuilderSidebar from "../Agents/Components/AgentBuilderSidebar";

const HASH_ROUTED_SECTIONS = ["profile", "content", "sharing"];

const DEFAULT_PROMPT_VALUES = {
  label: "",
  interpolationString: "",
  promptLength: 256,
  availableToAgents: [],
};

const camelize = (str) =>
  (str || "").replace(/\W+(.)/g, (match, chr) => chr.toUpperCase());

const PromptBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const promptId = params?.promptId;
  const isEditMode = !!promptId && promptId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace } = useWorkspaces();

  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const selectedPrompt = useMemo(
    () => state?.customPrompts?.find((u) => u?.id === promptId),
    [state?.customPrompts, promptId]
  );

  useEffect(() => {
    if (!promptId) {
      history.replace(`/workspaces/${workspaceId}/settings/generator_settings`);
    }
  }, [promptId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (promptId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    const promptConfig = history.location?.state?.promptConfig || null;
    setInitialMapValues(promptConfig || DEFAULT_PROMPT_VALUES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptId, isEditMode]);

  const prepareEditValues = useCallback(
    (prompt) => {
      const item = { ...prompt };
      if (item?.availableToAgents) {
        const enhanced = item.availableToAgents
          .map((agentId) => state?.agents?.find((u) => u?.id === agentId))
          .filter((u) => u != null)
          .map((u) => ({
            id: u.id,
            label: u.label,
            value: u.id,
          }));
        item.availableToAgents = enhanced;
      }
      return item;
    },
    [state?.agents]
  );

  useEffect(() => {
    if (!isEditMode) return;
    if (editInitRef.current === promptId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    if (!state?.customPrompts) return;
    const prompt = state.customPrompts.find((u) => u?.id === promptId);
    if (!prompt) {
      history.replace(
        `/workspaces/${activeWorkspaceId}/settings/generator_settings`
      );
      return;
    }
    editInitRef.current = promptId;
    setInitialMapValues(prepareEditValues(prompt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptId, isEditMode, state?.customPrompts, getActiveWorkspace()?.id]);

  const sectionGroups = useMemo(
    () => [
      {
        id: "setup",
        label: t("prompts.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "profile",
            title: t("prompts.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "content",
            title: t("prompts.builderSectionContent", {
              defaultValue: "Prompt",
            }),
            icon: AppAssets.icons.writer,
          },
          {
            id: "sharing",
            title: t("prompts.builderSectionSharing", {
              defaultValue: "Availability",
            }),
            icon: AppAssets.icons.taskAgent,
          },
        ],
      },
    ],
    [t]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_SECTIONS.includes(raw)) {
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/prompts/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("prompts.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // In edit mode, wait until the loaded values match the route id (see tools).
  const isFormReady =
    initialMapValues != null &&
    (!isEditMode || initialMapValues?.id === promptId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [activeSection]);

  const handlePromptItemSave = async (values, forkFromId) => {
    try {
      const availableToAgents = values?.availableToAgents?.map(
        (item) => item?.id || item
      );
      if (isEditMode && promptId) {
        const _editedPrompt = await Prompts.updatePrompt(promptId, [
          { property: "label", value: values.label },
          {
            property: "interpolationString",
            value: values.interpolationString,
          },
          { property: "availableToAgents", value: availableToAgents },
        ]);
        const updated = state?.customPrompts?.map((p) =>
          p?.id === promptId ? { ...p, ..._editedPrompt } : p
        );
        dispatch("SET_CUSTOM_PROMPTS", updated);
        AppToasty.show(t("prompts.created"), { type: "success" });
        formRef?.current?.resetForm?.({
          values: values || formRef.current?.values,
        });
        setSaveVersion((v) => v + 1);
      } else {
        const _suffix = generateRowId()?.toString();
        const _promptCode = `${camelize(values.label)}${
          forkFromId ? `-${_suffix}` : ""
        }`;
        const newPrompt = {
          label: values.label,
          promptLength: parseInt(values.promptLength) || 256,
          scope: _promptCode,
          type: _promptCode,
          availableToAgents: availableToAgents,
          interpolationString: values.interpolationString,
          workspaceID: getActiveWorkspace()?.id,
        };
        if (state?.customPrompts?.find((item) => item.type === _promptCode)) {
          return AppToasty.show(
            t("prompts.promptCreationErrorCaptionBecauseDuplicated"),
            { type: "danger" }
          );
        }
        const _newPrompt = await Prompts.createPrompt(newPrompt);
        dispatch("SET_CUSTOM_PROMPTS", [
          ...(state?.customPrompts || []),
          _newPrompt,
        ]);
        AppToasty.show(t("prompts.created"), { type: "success" });
        formRef?.current?.resetForm?.({ values });
        setSaveVersion((v) => v + 1);
        const newId =
          _newPrompt?.id || _newPrompt?.data?.createCustomPrompt?.id;
        if (newId) {
          createInitRef.current = false;
          history.replace(`/workspaces/${workspaceId}/prompts/${newId}`);
        }
      }
    } catch (error) {
      console.log("error", error);
      AppToasty.show(t("promptCreationErrorCaption"), { type: "danger" });
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingPrompts")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        <AgentBuilderSidebar
          groups={sectionGroups}
          activeSection={activeSection}
          onSelect={setActiveSection}
          errorSections={errorSections}
        />
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1, minWidth: 0 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <GenerationSettingsAI
            key={promptId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? promptId : undefined}
            settingType="prompt"
            handleGenerationItemSave={handlePromptItemSave}
            customPrompt={selectedPrompt}
            onDeleted={() =>
              history.push(
                `/workspaces/${workspaceId}/settings/generator_settings`
              )
            }
            setShowPromptGenerationModal={() => {}}
            isBuilderLayout
            builderActiveSection={activeSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(
              `/workspaces/${workspaceId}/settings/generator_settings`
            )
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.prompts")}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.label || selectedPrompt?.label || ""
            : history.location?.state?.promptConfig?.label ||
              t("prompts.builderNewPrompt", { defaultValue: "New prompt" })}
        </AppText>
        <View style={{ flex: 1 }} />
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("prompts.unsavedChangesSubCaption", {
          defaultValue:
            "This prompt has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default PromptBuilderScreenContainer;
